import React, { useEffect, useRef, useState } from "react";
import ProjectCard from "./ProjectCard";
import {
  certificates,
  education,
  experience,
  featuredProjects,
  hero,
  languages,
  otherProjects,
  profile,
  t,
  toolkit,
  ui,
} from "../data/portfolio";

/* ------------------------------------------------------------------ HERO */

export function Hero({ lang, onAsk }) {
  const labels = ui[lang];

  return (
    <header className="hero" id="top">
      <h1 className="hero-thesis">
        {t(hero.thesis, lang).map((line, i) => (
          <span key={i} className="hero-line">
            {line}
          </span>
        ))}
      </h1>

      <p className="hero-intro">{t(hero.intro, lang)}</p>

      <div className="hero-actions">
        <button type="button" className="btn btn--primary" onClick={() => onAsk()}>
          {labels.palette.ask}
        </button>
        <a className="btn" href="#work">
          {labels.nav.work}
        </a>
      </div>
    </header>
  );
}

/* ----------------------------------------------------------------- WORK */

export function Work({ lang }) {
  const labels = ui[lang];
  return (
    <section className="section" id="work">
      <div className="section-head">
        <h2 className="section-title">{labels.sections.work}</h2>
        <p className="section-note">{labels.sections.workNote}</p>
      </div>

      <div className="cards cards--featured">
        {featuredProjects.map((p) => (
          <ProjectCard key={p.slug} project={p} lang={lang} size="featured" />
        ))}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- LEDGER */

export function Ledger({ lang }) {
  return (
    <section className="section" id="ledger">
      <div className="section-head">
        <h2 className="section-title">{ui[lang].sections.ledger}</h2>
      </div>

      <div className="cards cards--compact">
        {otherProjects.map((p) => (
          <ProjectCard key={p.slug} project={p} lang={lang} size="compact" />
        ))}
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- EXPERIENCE */

export function Experience({ lang }) {
  return (
    <section className="section" id="experience">
      <div className="section-head">
        <h2 className="section-title">{ui[lang].sections.experience}</h2>
      </div>

      <ol className="rail">
        {experience.map((job, i) => (
          <li className={`rail-item ${job.current ? "is-current" : ""}`} key={i}>
            <span className="rail-period">{t(job.period, lang)}</span>
            <div className="rail-main">
              <h3 className="rail-org">{t(job.org, lang)}</h3>
              <p className="rail-role">
                {t(job.role, lang)}
                <i className="flow-rule" aria-hidden="true" />
                {t(job.place, lang)}
              </p>
              <p className="rail-summary">{t(job.summary, lang)}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* -------------------------------------------------------------- TOOLKIT */

export function Toolkit({ lang }) {
  return (
    <section className="section" id="toolkit">
      <div className="section-head">
        <h2 className="section-title">{ui[lang].sections.toolkit}</h2>
      </div>

      <div className="toolkit">
        {toolkit.map((group) => (
          <div className="tool-group" key={t(group.area, lang)}>
            <h3 className="tool-area">{t(group.area, lang)}</h3>
            <p className="flow">
              {group.items.map((item, i) => (
                <span key={i}>
                  {i > 0 && <i className="flow-rule" aria-hidden="true" />}
                  {t(item, lang)}
                </span>
              ))}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- ABOUT */

export function About({ lang }) {
  return (
    <section className="section" id="about">
      <div className="section-head">
        <h2 className="section-title">{ui[lang].sections.about}</h2>
      </div>

      <div className="about">
        <div className="about-school">
          <h3>{t(education.school, lang)}</h3>
          <p className="about-program">{t(education.program, lang)}</p>
          <p className="about-period">{education.period}</p>
          <p className="about-coursework">{t(education.coursework, lang)}</p>
        </div>

        <div className="about-side">
          <ul className="certs">
            {certificates.map((c) => (
              <li key={c.name}>
                <span className="cert-name">{c.name}</span>
                <span className="cert-issuer">{c.issuer}</span>
              </li>
            ))}
          </ul>
          <ul className="langs">
            {languages.map((l) => (
              <li key={t(l.name, lang)}>
                <span>{t(l.name, lang)}</span>
                <span className="lang-level">{t(l.level, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- CONTACT */

export function Contact({ lang, onAsk }) {
  const labels = ui[lang];
  return (
    <section className="section contact" id="contact">
      <h2 className="contact-title">{labels.sections.contact}</h2>
      <p className="contact-lead">{labels.contact.lead}</p>

      <div className="contact-actions">
        <a className="btn btn--primary" href={`mailto:${profile.email}`}>
          {labels.contact.emailAction}
        </a>
        <a className="btn" href={profile.telegram} target="_blank" rel="noreferrer">
          {labels.contact.telegramAction}
        </a>
        <button type="button" className="btn btn--ghost" onClick={() => onAsk()}>
          {labels.contact.askAction}
        </button>
      </div>

      <ul className="contact-list">
        <li>
          <a href={`mailto:${profile.email}`}>{profile.email}</a>
        </li>
        <li>
          <a href={profile.telegram} target="_blank" rel="noreferrer">
            Telegram {profile.telegramHandle}
          </a>
        </li>
        <li>
          <a href={profile.github} target="_blank" rel="noreferrer">
            github.com/Tetherium
          </a>
        </li>
        <li>
          <a href={profile.linkedin} target="_blank" rel="noreferrer">
            LinkedIn
          </a>
        </li>
        <li>
          <a href={profile.cvTr} target="_blank" rel="noreferrer">
            {labels.contact.cvTr}
          </a>
        </li>
        <li>
          <a href={profile.cvEn} target="_blank" rel="noreferrer">
            {labels.contact.cvEn}
          </a>
        </li>
      </ul>

      <p className="contact-foot">
        {profile.name} · {t(profile.location, lang)} · {t(profile.availability, lang)}
      </p>    </section>
  );
}

/* Bölüm görünür olduğunda gövdeye sınıf eklemek için küçük yardımcı */
export function useActiveSection(ids, key) {
  const [active, setActive] = useState(ids[0]);
  const ref = useRef(ids);
  ref.current = ids;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5] }
    );
    ref.current.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // key değişince (ör. proje sayfasından ana sayfaya dönünce) yeniden bağlan
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}
